/**
 * CardToTokenFlow Component
 * 
 * Multi-step component for card-to-token payment flow.
 * Handles NFC card reading, authorization, and token creation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CreditCard, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { platformApi } from '@/services/platformApi';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { useNFCBridge } from '@/hooks/useNFCBridge';
import { isCardToTokenEnabled } from '@/config/cardToTokenConfig';

export interface CardToTokenFlowProps {
  amount: number;
  currency: string;
  onSuccess: (tokenId: string, authCode: string) => void;
  onCancel: () => void;
  onError?: (error: string) => void;
}

type FlowStep = 'initialize' | 'nfc' | 'authorizing' | 'success' | 'error';

interface CardData {
  pan: string;
  lastFour: string;
  expiryMonth: number;
  expiryYear: number;
  cardholderName: string;
  cardBrand: string;
  cvv?: string;
}

export const CardToTokenFlow: React.FC<CardToTokenFlowProps> = ({
  amount,
  currency,
  onSuccess,
  onCancel,
  onError,
}) => {
  const [step, setStep] = useState<FlowStep>('initialize');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [cardData, setCardData] = useState<CardData | null>(null);
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  // Check for NFC bridge availability
  const { isAvailable: nfcAvailable, isChecking: nfcChecking } = useNFCBridge();
  
  // Check if feature is enabled
  const featureEnabled = isCardToTokenEnabled();

  // Initialize session
  useEffect(() => {
    if (!featureEnabled) {
      setError('Card-to-token feature is not enabled');
      setStep('error');
      return;
    }

    const initializeSession = async () => {
      try {
        setIsProcessing(true);
        const response = await platformApi.payments.initializeCardToToken({
          amount,
          currency,
        });
        setSessionId(response.sessionId);
        setStep('nfc');
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to initialize payment session';
        setError(errorMessage);
        setStep('error');
        onError?.(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    };

    if (step === 'initialize') {
      initializeSession();
    }
  }, [step, amount, currency, onError, featureEnabled]);

  // Handle NFC card read
  const handleNFCCardRead = useCallback(async (cardData: CardData) => {
    if (!sessionId) {
      setError('Session not initialized');
      setStep('error');
      return;
    }

    setCardData(cardData);
    setStep('authorizing');
    setIsProcessing(true);

    try {
      const response = await platformApi.payments.processCardAuthorization({
        sessionId,
        cardData: {
          pan: cardData.pan,
          lastFour: cardData.lastFour,
          expiryMonth: cardData.expiryMonth,
          expiryYear: cardData.expiryYear,
          cardholderName: cardData.cardholderName,
          cardBrand: cardData.cardBrand,
          cvv: cardData.cvv || '',
        },
      });

      if (response.success && response.authCode) {
        setAuthCode(response.authCode);
        setStep('success');
        toast({
          title: 'Authorization Successful',
          description: `Authorization code: ${response.authCode}`,
        });
        // Call onSuccess after a short delay
        setTimeout(() => {
          onSuccess(sessionId, response.authCode!);
        }, 1500);
      } else {
        const errorMessage = response.error || 'Authorization failed';
        setError(errorMessage);
        setStep('error');
        onError?.(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to process authorization';
      setError(errorMessage);
      setStep('error');
      onError?.(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [sessionId, toast, onSuccess, onError]);

  // Start NFC reading
  const startNFCReading = useCallback(async () => {
    const vaiuNFC = (window as any).VaiuNFC;
    if (!vaiuNFC) {
      setError('NFC bridge not available. Please install the VAIU NFC Bridge app.');
      setStep('error');
      return;
    }

    try {
      setIsProcessing(true);
      const result = await vaiuNFC.readCard({
        amount,
        currency,
      });

      if (result.success && result.cardData) {
        const data: CardData = {
          pan: result.cardData.pan || result.cardData.cardNumber || '',
          lastFour: result.cardData.lastFour || result.cardData.pan?.slice(-4) || '',
          expiryMonth: result.cardData.expiryMonth || parseInt(result.cardData.expiryDate?.split('/')[0] || '0'),
          expiryYear: result.cardData.expiryYear || parseInt(result.cardData.expiryDate?.split('/')[1] || '0'),
          cardholderName: result.cardData.cardholderName || '',
          cardBrand: result.cardData.cardBrand || result.cardData.brand || 'Unknown',
          cvv: result.cardData.cvv,
        };
        await handleNFCCardRead(data);
      } else {
        throw new Error(result.error || 'Failed to read card');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to read card via NFC';
      setError(errorMessage);
      setStep('error');
      onError?.(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [amount, currency, handleNFCCardRead, onError]);

  // Manual card entry fallback
  const handleManualEntry = () => {
    // For now, show error - manual entry can be added later
    setError('Manual card entry not yet implemented. Please use NFC tap.');
    setStep('error');
  };

  const renderContent = () => {
    switch (step) {
      case 'initialize':
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p className="text-sm text-muted-foreground">Initializing payment session...</p>
          </div>
        );

      case 'nfc':
        return (
          <div className="flex flex-col items-center justify-center p-8 space-y-4">
            <CreditCard className="h-16 w-16 text-primary mb-4" />
            <h3 className="text-lg font-semibold">Tap Your Card</h3>
            <p className="text-sm text-muted-foreground text-center">
              Hold your card near the back of your phone
            </p>
            {nfcChecking ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Checking NFC availability...</span>
              </div>
            ) : nfcAvailable ? (
              <div className="space-y-2">
                <Button
                  onClick={startNFCReading}
                  disabled={isProcessing}
                  className="w-full"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Reading Card...
                    </>
                  ) : (
                    'Start NFC Reading'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleManualEntry}
                  className="w-full"
                >
                  Enter Card Manually
                </Button>
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  NFC Bridge app not detected. Please install the VAIU NFC Bridge app to use this feature.
                </AlertDescription>
              </Alert>
            )}
            <Button variant="ghost" onClick={onCancel} className="mt-4">
              Cancel
            </Button>
          </div>
        );

      case 'authorizing':
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p className="text-sm text-muted-foreground">Processing authorization...</p>
            {cardData && (
              <div className="mt-4 text-xs text-muted-foreground">
                Card: •••• {cardData.lastFour}
              </div>
            )}
          </div>
        );

      case 'success':
        return (
          <div className="flex flex-col items-center justify-center p-8 space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
            <h3 className="text-lg font-semibold">Authorization Successful</h3>
            {authCode && (
              <p className="text-sm text-muted-foreground">
                Authorization Code: <span className="font-mono font-semibold">{authCode}</span>
              </p>
            )}
            <p className="text-sm text-muted-foreground text-center">
              Creating your token...
            </p>
          </div>
        );

      case 'error':
        return (
          <div className="flex flex-col items-center justify-center p-8 space-y-4">
            <XCircle className="h-16 w-16 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold">Authorization Failed</h3>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="flex gap-2">
              <Button onClick={() => setStep('nfc')} variant="outline">
                Try Again
              </Button>
              <Button onClick={onCancel} variant="ghost">
                Cancel
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card rounded-lg border p-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold mb-2">Card to Token</h2>
          <p className="text-sm text-muted-foreground">
            Amount: {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency,
            }).format(amount)}
          </p>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

